import { pickLocalized, type Locale, type Profile } from '@spot/shared';
import { useTranslation } from 'react-i18next';

import { Avatar } from '../../components/ui/avatar';
import { splitInterests } from './shared-interests';

const NO_INTERESTS: readonly string[] = [];

/**
 * The profile card shared by S12 (own) and S13 (public). User text is rendered as plain text.
 * `viewerInterests`: the viewer's slugs, to bring out what they share with someone else (T-1336).
 */
export function ProfileCard({
	profile,
	locale,
	viewerInterests = NO_INTERESTS,
}: {
	profile: Profile;
	locale: Locale;
	viewerInterests?: readonly string[];
}) {
	const { t } = useTranslation();
	const { shared, rest } = splitInterests(
		profile.interests,
		profile.isMe ? NO_INTERESTS : viewerInterests,
	);
	const facts = [
		profile.age === null ? null : t('profile.age', { age: profile.age }),
		profile.city === null ? null : pickLocalized(profile.city.name, locale),
	].filter((fact): fact is string => fact !== null);
	const { stats } = profile;

	return (
		<div className="flex flex-col gap-5">
			<div className="flex flex-col items-center gap-2 text-center">
				<Avatar name={profile.displayName} photoUrl={profile.photoUrl} size="size-24" />
				<h1 className="text-title text-text font-bold break-words">{profile.displayName}</h1>
				{facts.length > 0 && <p className="text-body text-text-muted">{facts.join(' · ')}</p>}
				{/* Stats as tiles with a big emoji instead of one line of text (T-1311). */}
				<ul className="mt-1 flex justify-center gap-2">
					{[
						stats.rating === null
							? null
							: { emoji: '⭐', text: t('profile.rating', { rating: stats.rating }) },
						{ emoji: '🎟️', text: t('profile.visited', { count: stats.visited }) },
						{ emoji: '✨', text: t('profile.created', { count: stats.created }) },
					]
						.filter((stat): stat is { emoji: string; text: string } => stat !== null)
						.map((stat) => (
							<li
								key={stat.emoji}
								className="bg-surface border-border rounded-card animate-pop flex min-w-24 flex-col items-center gap-1 border px-3 py-2"
							>
								<span aria-hidden="true" className="text-[28px] leading-none">
									{stat.emoji}
								</span>
								<span className="text-caption text-text-muted">{stat.text}</span>
							</li>
						))}
				</ul>
			</div>

			{profile.about !== null && (
				<section>
					<h2 className="text-caption text-text-muted mb-1">{t('profile.about')}</h2>
					<p className="text-body text-text whitespace-pre-wrap break-words">{profile.about}</p>
				</section>
			)}

			<section>
				<h2 className="text-caption text-text-muted mb-2">{t('profile.interests')}</h2>
				{profile.interests.length === 0 ? (
					<p className="text-body text-text-muted">{t('profile.no_interests')}</p>
				) : (
					<>
						{shared.length > 0 && (
							<p className="text-caption text-primary mb-2 font-semibold">
								{t('profile.shared_interests', { count: shared.length })}
							</p>
						)}
						<ul className="flex flex-wrap gap-2">
							{shared.map((interest) => (
								<li
									key={interest.slug}
									data-shared="true"
									className="text-body from-primary to-primary/70 shadow-primary/40 text-text rounded-full border border-transparent bg-gradient-to-br px-3.5 py-1.5 font-semibold shadow-lg"
								>
									{pickLocalized(interest.name, locale)}
								</li>
							))}
							{rest.map((interest) => (
								<li
									key={interest.slug}
									className="text-body border-border text-text rounded-full border px-3.5 py-1.5"
								>
									{pickLocalized(interest.name, locale)}
								</li>
							))}
						</ul>
					</>
				)}
			</section>
		</div>
	);
}
